import React, { useState, useEffect, Fragment } from "react";
import cx from "classnames";
import parse from "html-react-parser";
import GoogleMapReact from "google-map-react";
import { useParams } from "react-router-dom";
import { slugify } from "../../helpers";
import Page from "../../layouts/Page";
import TextBlock from "../../components/TextBlock";
import Button from "../../components/Button";
import SocialIcons from "../../components/SocialIcons";
import styles from "./styles.module.sass";
import { hostname, mapStyles } from "../../constants";

import Map1 from "./access-mrt1.jpg";
import Map2 from "./access-mrt2.jpg";
import Map3 from "./access-mrt3.jpg";
import Map4 from "./access-mrt4.jpg";
import BaliMap1 from "./access-bali1.jpg";
import BaliMap2 from "./access-bali2.jpg";
import BaliMap3 from "./access-bali3.jpg";
import BaliMap4 from "./access-bali4.jpg";

import AccessAirportUrl from "./access-airport.svg?url";
import MapMarkerURL from "./map-marker.svg?react";

interface LocationEntry {
  location: string;
  latitude: string;
  longitude: string;
  address: string;
  telephone: string;
  fax?: string;
  email: string;
}

interface MarkerProps {
  className?: string;
}

const Marker: React.FC<MarkerProps> = ({ className }) => (
  <MapMarkerURL
    className={className}
    width={35}
    height={45}
    aria-label="Location marker"
  />
);

const Accessibility: React.FC = () => {
  const { country } = useParams<{ country?: string }>();
  const [location, setLocation] = useState<LocationEntry | null>(null);

  useEffect(() => {
    fetch(`${hostname}/wp-json/wp/v2/pages/72873?_embed`)
      .then((res) => res.json())
      .then((data) => {
        const found = data.acf.location.find(
          (loc: LocationEntry) => slugify(loc.location) === country,
        );
        setLocation(found || null);
      })
      .catch(() => setLocation(null));
  }, [country]);

  if (!location) return null;

  return (
    <Page type="local" className={styles.base} country={country}>
      <div className={styles.map}>
        <GoogleMapReact
          bootstrapURLKeys={{ key: "AIzaSyCWTOXL9FZuhpVR10hx4CLsgE15PYxqNmo" }}
          defaultCenter={{
            lat: Number(location.latitude),
            lng: Number(location.longitude),
          }}
          defaultZoom={16}
          options={{ styles: mapStyles }}
        >
          <Marker className={styles.marker} />
        </GoogleMapReact>
      </div>

      <div className={styles.address}>
        <p>{parse(location.address)}</p>
        <p>
          Tel: {location.telephone}
          {location.fax && ` | Fax: ${location.fax}`}
        </p>
        <p>
          Email: <a href={`mailto:${location.email}`}>{location.email}</a>
        </p>
        <SocialIcons
          containerClassName={styles.socialContainer}
          showTA={country === "singapore"}
        />
      </div>

      <div className={styles.directions}>
        {country === "singapore" && (
          <Fragment>
            <TextBlock>
              <p>
                Lloyd’s Inn does not provide a shuttle service but public
                transportation from the airport to our inn is convenient and
                safe.
              </p>
              <p>
                <strong>TAXI: </strong>A taxi from Singapore Changi Airport is a
                25 minutes ride,
                <br />
                and will cost no more than SGD$35 (standard taxi)
              </p>
              <p>
                <strong>BUS: </strong>From Singapore Changi Airport by bus, it
                is a 60mins ride and will cost
                <br />
                no more than SGD$2.50. Board bus 36 at the Changi Airport
                Terminal 2, <br />
                PTBS Basement and alight at Winsland House, Penang Road, 26
                stops later. <br />
                Walk approximately 280m to Lloyd’s Inn.
              </p>
              <p>
                <strong>TRAIN:</strong> Refer to diagram below.
              </p>
            </TextBlock>
            <img
              src={AccessAirportUrl}
              style={{
                width: "100%",
                maxWidth: 1364,
                height: "auto",
                margin: "1rem 0 5rem",
              }}
              alt="Directions diagram"
            />
            <div>
              <TextBlock>
                <strong>DIRECTIONS FROM SOMERSET MRT</strong>
              </TextBlock>
              <div className={styles.guide}>
                {[
                  {
                    id: 1,
                    src: Map1,
                    label: "Directions: Step 1",
                  },
                  {
                    id: 2,
                    src: Map2,
                    label: "Directions: Step 2",
                  },
                  {
                    id: 3,
                    src: Map3,
                    label: "Directions: Step 3",
                  },
                  {
                    id: 4,
                    src: Map4,
                    label: "Directions: Step 4",
                  },
                ].map(({ id, src, label }) => (
                  <div key={id} className={styles.guideImage}>
                    <img src={src} alt={label} />
                  </div>
                ))}
              </div>
              <TextBlock>
                <p>
                  Take Exit A to get onto Exeter Road. Walk 150m down Exeter
                  Road and turn right onto Killiney Road. Walk 200m down
                  Killiney Road and turn left onto Lloyd Road. Walk 250m to the
                  end of Lloyd Road & you&rsquo;ll find us on the left side of
                  the road.
                </p>
              </TextBlock>
            </div>
          </Fragment>
        )}

        {(country === "bali" || country === "suites") && (
          <Fragment>
            <TextBlock>
              <p>
                Lloyd’s Inn does not provide a shuttle service but public <br />
                transportation from the airport to our inn is convenient and
                safe.
              </p>
              <p>
                Ngurah Rai International Airport is about 8km away. A taxi will{" "}
                <br />
                take you about 30mins to arrive at an estimated Rp. 200,000.
              </p>
            </TextBlock>
            <TextBlock width={1364}>
              <p>
                <Button href="/images/directional-guide-bali.jpg">
                  Download directional guide
                </Button>
              </p>
            </TextBlock>
            <div className={cx(styles.guide, styles.guideBali)}>
              {[
                {
                  src: BaliMap1,
                  label: "Jl. by pass Ngurah Rai - Jl. Sunset Road",
                },
                {
                  src: BaliMap2,
                  label: "Jl. Sunset Road - Jl. Nakula",
                },
                {
                  src: BaliMap3,
                  label: "Jl. Arjuna (Double Six Street)",
                },
                {
                  src: BaliMap4,
                  label: "Lloyd’s Inn Bali is on the right",
                },
              ].map(({ src, label }) => (
                <div key={src} className={styles.guideImage}>
                  <img src={src} alt={label} />
                  {label}
                  {src === BaliMap4 && (
                    <>
                      <br />
                      (Look out for Sicilia Spa)
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className={styles.openingHours}>
              <div>
                <p>
                  <strong>BISTRO (OPEUM BISTRO):</strong>
                  <br />
                  12pm - 11pm (all day dining)
                </p>
                <p>
                  <strong>BAR:</strong>
                  <br />
                  4pm - 11pm (last order)
                </p>
              </div>
              <div>
                <p>
                  <strong>FRONT DESK:</strong>
                  <br />
                  24/7
                </p>
                <p>
                  <strong>LOUNGE (THE HIDEOUT):</strong>
                  <br />
                  9am - 9pm
                </p>
                <p>
                  <strong>MEETING ROOM:</strong>
                  <br />
                  Prior booking required
                </p>
              </div>
              <div>
                <p>
                  <strong>POOL:</strong>
                  <br />
                  9am - 9pm
                </p>
                <p>
                  <strong>SPA (THE CANOPY SPA):</strong>
                  <br />
                  9am - 11pm
                </p>
              </div>
            </div>
            <TextBlock>
              <p>
                Please note: As much as we are animal lovers, pets are not
                allowed (neither are durians) to be sensitive to other guests
                who may have allergies.{" "}
              </p>
            </TextBlock>
          </Fragment>
        )}

        {country === "kuala-lumpur" && (
          <Fragment>
            <TextBlock>
              <p>
                <strong>TAXI:</strong> Kuala Lumpur International Airport is
                about 66km away. A taxi will <br />
                take you about 1 hour to arrive at an estimated RM60 - RM80
              </p>
            </TextBlock>
            <TextBlock width={1364}>
              <p>
                <strong>PUBLIC TRANSPORT:</strong> Take the high speed train
                ‘KLIA Ekspres’ from
                <br /> Kuala Lumpur International Airport to KL Sentral station
                (approx. 30 mins).
                <br />
                Change to the local LRT to Pasar Seni Station and change to MRT
                Kajang.
                <br />
                Take the train to ‘MRT Tun Razak Exchange”. Get off and walk 6
                mins to Lloyd’s Inn KL.
                <br />
                Alternatively, you can take a taxi or ‘Grab’ car from KL Sentral
                station (approx. 20 minutes ride).
              </p>
            </TextBlock>
            <TextBlock>
              <p>
                <strong>LIMO TRANSFERS:</strong> Private transfers will cost
                between MYR300 – MYR600 per car (depending on vehicle type)
                one-way from Kuala Lumpur International Airport to Lloyd’s Inn
                or vice versa.{" "}
              </p>
            </TextBlock>
            <TextBlock>
              <p>
                Please contact us at{" "}
                <a href="mailto:kualalumpur@lloydsinn.com">
                  kualalumpur@lloydsinn.com
                </a>{" "}
                if you require further assistance.
              </p>
            </TextBlock>
            <div className={styles.openingHours}>
              <div>
                <p>
                  <strong>FRONT DESK:</strong>
                  <br />
                  24/7
                </p>
                <p>
                  <strong>POOL:</strong>
                  <br />
                  9am - 9pm
                </p>
              </div>
              <div>
                <p>
                  <strong>SKY TERRACE 1 & 2:</strong>
                  <br />
                  24/7
                </p>
                <p>
                  <strong>BISTRO (OPEUM BISTRO):</strong>
                  <br />
                  6.30am - 9.30pm
                </p>
                <p>
                  <strong>PRAYER ROOM:</strong>
                  <br />
                  24/7
                </p>
              </div>
              <div>
                <p>
                  <strong>THE SOCIAL POD:</strong>
                  <br />
                  9am - 9pm
                </p>

                <p>
                  <strong>THE TEA ROOM:</strong>
                  <br />
                  6.30am - 9.30pm
                </p>
              </div>
            </div>
            <TextBlock>
              <p>
                Please note: As much as we are animal lovers, pets are not
                allowed (neither are durians) to be sensitive to other guests
                who may have allergies.{" "}
              </p>
            </TextBlock>
          </Fragment>
        )}
      </div>
    </Page>
  );
};

export default Accessibility;
